import { Injectable } from '@angular/core';

export interface ConsoleErrorEntry {
  message: string;
  timestamp: string;
  stack?: string;
}

const MAX_ERRORS = 100;

@Injectable({ providedIn: 'root' })
export class FeedbackConsoleInterceptorService {
  private readonly errors: ConsoleErrorEntry[] = [];
  private readonly originalConsoleError = console.error.bind(console);

  constructor() {
    this.patchConsoleError();
    this.patchWindowOnError();
  }

  getErrors(): ConsoleErrorEntry[] {
    return [...this.errors];
  }

  clear(): void {
    this.errors.length = 0;
  }

  private patchConsoleError(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.error = (...args: any[]) => {
      this.capture(args);
      this.originalConsoleError(...args);
    };
  }

  private patchWindowOnError(): void {
    window.addEventListener('error', (event: ErrorEvent) => {
      this.capture([event.message], event.error?.stack);
    });
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      this.capture(
        [`Unhandled Promise Rejection: ${message}`],
        reason instanceof Error ? reason.stack : undefined
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private capture(args: any[], stack?: string): void {
    if (this.errors.length >= MAX_ERRORS) return;

    const message = args
      .map(a => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'object') {
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        }
        return String(a);
      })
      .join(' ');

    this.errors.push({
      message,
      timestamp: new Date().toISOString(),
      stack: stack ?? (args[0] instanceof Error ? args[0].stack : undefined),
    });
  }
}
